import { isNil } from 'lodash';

import type { AiAction } from '~/components/AiChat/types';
import { isDynamicClaimActionTaskData } from '~/components/AiChat/types';

interface ExposureIdentity {
  id?: number | string;
  claim_internal_id?: number | string;
  claim_id_display_with_exposure_suffix?: string;
  label_text?: string;
}

/**
 * The exposure an action names, as the agent named it. A dynamic claim action carries no
 * `related_exposure_id`: its exposure is the payload's `exposure_id`, which `execute_action_tool`
 * reads. An empty one names no exposure, as the action treats it.
 */
export const getActionExposureRef = (action: AiAction | undefined): string | undefined => {
  const payload = isDynamicClaimActionTaskData(action?.task_data) ? action.task_data.envelope?.payload : undefined;
  const exposureRef = action?.related_exposure_id ?? payload?.exposure_id;
  return isNil(exposureRef) || exposureRef === '' ? undefined : String(exposureRef);
};

const normalize = (identifier: unknown) => String(identifier).trim().toLowerCase();

// Every form the server's exposure resolution accepts: the number, the `<claim>-<NN>` handle, the
// label and the database id.
const identifiers = (exposure: ExposureIdentity) =>
  new Set(
    [exposure.claim_internal_id, exposure.claim_id_display_with_exposure_suffix, exposure.label_text, exposure.id]
      .filter((form) => !isNil(form) && String(form) !== '')
      .map(normalize)
  );

/** The one exposure the action names; none when no exposure, or more than one, carries that identifier. */
export const findActionExposure = <T extends ExposureIdentity>(
  action: AiAction | undefined,
  exposures: T[] | undefined
): T | undefined => {
  // A legacy action's `related_exposure_id` is the database id the server reads, nothing else.
  if (!isNil(action?.related_exposure_id)) {
    return exposures?.find((exposure) => String(exposure?.id) === String(action.related_exposure_id));
  }

  const exposureRef = getActionExposureRef(action);
  if (isNil(exposureRef)) return undefined;

  const wanted = normalize(exposureRef);
  const matches = wanted ? (exposures ?? []).filter((exposure) => exposure && identifiers(exposure).has(wanted)) : [];
  return matches.length === 1 ? matches[0] : undefined;
};
